import { FC } from 'react';
import { Row, Col, Modal } from 'antd';
import Button from '../../button/Button';
import SaveIcon from '../../../assets/icons/SaveIcon';
import ArrowIcon from '../../../assets/icons/ArrowIcon';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { finishedOnboarding, saveAsDraft, saveAsPublish } from '../../../redux/reducers/onboarding/actionAsyncThunk';
import { useLocation } from 'react-router-dom';

import { ROUTER_PATHS } from '../../../navigation/routerPaths';
import { setIsOpenOnboardingLastStepModal } from '../../../redux/reducers/onboarding/sliceReducer';
import WarningIcon from '../../../assets/icons/WarningIcon';
import { OnboardingRequestStatuses } from '../../../common/api/services/OnboardingApi/enums/OnboardingRequestStatuses';
import { getIsHideDraftBtn } from '../../../common/utils/getIsHideDraftBtn';
import { historyApi } from '../../../common/api/services/HistoryApi/historyApi';
import { FieldKeys } from '../../../common/api/services/HistoryApi/types/fieldKeys';
import {
    setActiveKeys,
    setAllFields,
    setHistoryErrorMessages,
} from '../../../redux/reducers/onboarding-history/sliceReducer';
import { TabKeyByPath } from '../../../common/api/services/OnboardingApi/enums/OnboardingRequestKeys';
import { openErrorNotification } from '../../notification/Notification';
import { setAllFieldsWC } from '../../../redux/reducers/onboarding-history-WC/sliceReducer';

const {
    workPermit,
    workContract,
    bankAccount,
    childAllowance,
    personalInformation,
    onboarding,
    clientContract,
    avsAhv,
    additionalInformation,
} = ROUTER_PATHS;

const OnboardingFooterV2: FC = () => {
    const pathName = useLocation().pathname;
    const dispatch = useAppDispatch();

    const { tabStatus } = useAppSelector((state) => state.onboarding);
    const isHideDraftBtn = getIsHideDraftBtn(pathName, tabStatus);
    const { hasWorkPermit, selfHandledWorkPermit, basisOfLegalStay } = useAppSelector(
        (state) => state.onboarding.workPermit
    );
    const hasBankAccount = useAppSelector((state) => state.onboarding.bankAccount.hasBankAccount);
    const hasSsn = useAppSelector((state) => state.onboarding.ssnInformation.hasSSN);
    const isOpen = useAppSelector((state) => state.onboarding.isOpenOnboardingLastStepModal);
    const { hasSwissAddress, hasAbroadAddress, hasOtherJob, hasCBA, hasTreasury } = useAppSelector(
        (state) => state.onboarding.personalInfo
    );

    const isSwissNationality = useAppSelector((state) => state.onboarding.personalInfo.nationality === 'Switzerland');
    const personalInfo = useAppSelector((state) => state.onboarding.personalInfo);
    const { workForm, nationality } = personalInfo;
    const isCorporation = workForm === 'corporation';

    const activeFieldsOnboarding = useAppSelector((state) => state.onBoardingHistory.activeFields);
    const historyFieldsOnboarding = useAppSelector((state) => state.onBoardingHistory.historyFields);
    const historyFieldsWC = useAppSelector((state) => state.onBoardingHistoryWC.historyFields);
    const isSwiss = nationality === 'Switzerland';
    const isWorkPermitSwiss = pathName.includes(workPermit) && isSwiss;

    const activeFieldsWC = useAppSelector((state) => state.onBoardingHistoryWC.activeFields);

    const isWorkContractPage = pathName.includes(workContract);
    const activeFields = isWorkContractPage ? activeFieldsWC : activeFieldsOnboarding;

    const historyFields = isWorkContractPage ? historyFieldsWC : historyFieldsOnboarding;

    const isSkipConditions =
        (pathName.includes(bankAccount) && !hasBankAccount) ||
        (pathName.includes(avsAhv) && (!hasSsn || isCorporation)) ||
        (pathName.includes(workPermit) &&
            (activeFields[FieldKeys.workPermit]?.find((item: any) =>
                !item.isSwissNationality && !item.hasWorkPermit && !item.selfHandledWorkPermit && item.basisOfLegalStay
                    ? false
                    : !item.hasWorkPermit
            ) ||
                historyFields[FieldKeys.workPermit]?.find((item: any) =>
                    !item.isSwissNationality &&
                    !item.hasWorkPermit &&
                    !item.selfHandledWorkPermit &&
                    item.basisOfLegalStay
                        ? false
                        : !item.hasWorkPermit
                )));

    const isPageHasHistory = [personalInformation, workContract, bankAccount, workPermit].some((path) =>
        pathName.includes(path)
    );

    const currentPage = [personalInformation, workContract, bankAccount, workPermit].find((path) =>
        pathName.includes(path)
    ) as keyof typeof TabKeyByPath;

    const buttonText = isSkipConditions ? 'Skip and go next' : 'Save and publish';

    // const saveAsPublishHandler = () => {
    //   dispatch(saveAsPublish());
    // };

    const combineFields = () => {
        const result: Record<string, Array<Record<string, unknown>>> = {};

        Object.entries(activeFields).forEach(([key, value]) => {
            if (!result[key]) result[key] = [];
            result[key] = result[key].concat(
                value.map((item) => ({
                    ...item,
                    isActive: true,
                }))
            );
        });

        Object.entries(historyFields).forEach(([key, value]) => {
            if (!result[key]) result[key] = [];
            result[key] = result[key].concat(
                value.map((item) => ({
                    ...item,
                    isActive: false,
                }))
            );
        });

        //NOTE: remove field with spesial conditions
        //Work Permit for no has work permit
        if (result[FieldKeys.workPermit]?.length) {
            result[FieldKeys.workPermit] = result[FieldKeys.workPermit].map((item) => {
                if (item.hasWorkPermit) {
                    return item;
                }
                return {
                    ...item,
                    documentType: null,
                    permitType: null,
                    permitCanton: null,
                    basisOfLegalStay: item.basisOfLegalStay || null,
                };
            });
        }
        //PI
        if (
            !personalInfo.hasSwissAddress &&
            (activeFields[FieldKeys.swissAddress] || historyFields[FieldKeys.swissAddress])
        ) {
            result[FieldKeys.swissAddress] = [];
        }

        if (
            !personalInfo.hasAbroadAddress &&
            (activeFields[FieldKeys.abroadAddress] || historyFields[FieldKeys.abroadAddress])
        ) {
            result[FieldKeys.abroadAddress] = [];
        }
        if (!personalInfo.hasCBA && (activeFields[FieldKeys.cba] || historyFields[FieldKeys.cba])) {
            result[FieldKeys.cba] = [];
        }

        if (result[FieldKeys.maritalStatus]) {
            result[FieldKeys.maritalStatus] = result[FieldKeys.maritalStatus].map((item) => {
                if (item.maritalStatus !== 'Married') {
                    return {
                        ...item,
                        maritalStatusDetails: {},
                    };
                }
                return item;
            });
        }

        if (!personalInfo.hasOtherJob && (activeFields[FieldKeys.otherJob] || historyFields[FieldKeys.otherJob])) {
            result[FieldKeys.otherJob] = [];
        }

        if (!personalInfo.hasTreasury && (activeFields[FieldKeys.treasury] || historyFields[FieldKeys.treasury])) {
            result[FieldKeys.treasury] = [];
        }

        return result;
    };
    const openPITabs = () => {
        if (pathName.includes(personalInformation)) {
            dispatch(setActiveKeys(['1', '2', '3', '4', '5']));
        }
    };

    const saveAsPublishHandler = () => {
        openPITabs();
        if (isPageHasHistory && !isWorkPermitSwiss) {
            historyApi
                .updateHistory({
                    status: OnboardingRequestStatuses.published,
                    id: 'consultant',
                    matchedKeys: combineFields(),
                    tab: TabKeyByPath[currentPage],
                    options: { hasAbroadAddress, hasSwissAddress, hasOtherJob, hasCBA, hasTreasury },
                })
                .then((resp) => {
                    if (currentPage === workContract) {
                        dispatch(setAllFieldsWC(resp));
                    } else {
                        dispatch(setAllFields(resp));
                    }
                    dispatch(setHistoryErrorMessages({ messages: [], tabName: TabKeyByPath[currentPage] }));
                })
                .then(() => {
                    dispatch(saveAsPublish());
                })
                .catch((e: any) => {
                    const message = e?.response?.data?.message;
                    if (message) {
                        openErrorNotification(message);
                        if (message.toLowerCase().includes('swiss') || message.toLowerCase().includes('abroad')) {
                            dispatch(setActiveKeys(['1']));
                        }
                    }
                    dispatch(
                        setHistoryErrorMessages({
                            messages: e?.response?.data?.messages,
                            tabName: TabKeyByPath[currentPage],
                        })
                    );
                });
        } else {
            dispatch(saveAsPublish());
        }
    };

    const saveAsDraftHandler = () => {
        openPITabs();
        if (isPageHasHistory) {
            historyApi
                .updateHistory({
                    status: OnboardingRequestStatuses.draft,
                    id: 'consultant',
                    matchedKeys: combineFields(),
                    tab: TabKeyByPath[currentPage],
                    options: { hasAbroadAddress, hasSwissAddress, hasOtherJob, hasCBA, hasTreasury },
                })
                .then((resp) => {
                    if (currentPage === workContract) {
                        dispatch(setAllFieldsWC(resp));
                    } else {
                        dispatch(setAllFields(resp));
                    }
                    dispatch(setHistoryErrorMessages({ messages: [], tabName: TabKeyByPath[currentPage] }));
                })
                .then(() => {
                    dispatch(saveAsDraft());
                })
                .catch((e: any) => {
                    dispatch(
                        setHistoryErrorMessages({
                            messages: e?.response?.data?.messages,
                            tabName: TabKeyByPath[currentPage],
                        })
                    );
                });
        } else {
            dispatch(saveAsDraft());
        }
    };

    const isWarningText = tabStatus.some((el) => el === OnboardingRequestStatuses.toConfirm);

    return (
        <Row align="middle" className="onboarding-footer" justify="space-between">
            <Col span={15}>
                {isHideDraftBtn && (
                    <p>
                        By clicking &quot;Save as Draft&quot;, you will be redirected to the next step and the
                        information filled in will be saved and visible only to you. By clicking &quot;Save and
                        Publish&quot;, the information will be saved and visible for Numeriq.
                    </p>
                )}
            </Col>
            <Col span={9}>
                <Row gutter={[16, 0]} justify="end">
                    {!isSkipConditions && isHideDraftBtn && (
                        <Col>
                            <Button
                                buttonStyle="secondary"
                                buttonSize="normal"
                                leftIcon={<SaveIcon />}
                                onClick={saveAsDraftHandler}
                            >
                                Save as Draft
                            </Button>
                        </Col>
                    )}
                    <Col>
                        <Button
                            buttonStyle="primary"
                            buttonSize="normal"
                            rightIcon={<ArrowIcon />}
                            onClick={saveAsPublishHandler}
                        >
                            {buttonText}
                        </Button>
                    </Col>
                </Row>
            </Col>

            {isOpen && (
                <Modal
                    width={490}
                    open={isOpen}
                    centered
                    footer={null}
                    destroyOnClose={true}
                    wrapClassName="footer-edit-lead-modal-window-wrapper"
                >
                    <p>Congratulations! You have successfully covered the Onboarding. Do you want to finish?</p>

                    {isWarningText && (
                        <div className="onboarding-warning">
                            <div style={{ display: 'flex' }}>
                                <WarningIcon />
                            </div>
                            <p>
                                You need to update the portal within 15 days if you have changes in family status,
                                address, birth of a child, spouse’s working status.
                            </p>
                        </div>
                    )}
                    <Row gutter={[16, 0]}>
                        <Col span={12}>
                            <Button
                                buttonWidth="100%"
                                buttonSize="normal"
                                buttonStyle="secondary"
                                onClick={() => dispatch(setIsOpenOnboardingLastStepModal(false))}
                            >
                                No, stay here
                            </Button>
                        </Col>

                        <Col span={12}>
                            <Button
                                buttonWidth="100%"
                                buttonSize="normal"
                                buttonStyle="primary"
                                onClick={() => {
                                    dispatch(finishedOnboarding());
                                }}
                            >
                                Yes, finish
                            </Button>
                        </Col>
                    </Row>
                </Modal>
            )}
        </Row>
    );
};

export default OnboardingFooterV2;

const getBackRoute = (path: string): string => {
    if (path.includes(workPermit)) {
        return onboarding + personalInformation;
    }

    if (path.includes(workContract)) {
        return onboarding + workPermit;
    }

    if (path.includes(clientContract)) {
        return onboarding + workContract;
    }

    if (path.includes(bankAccount)) {
        return onboarding + clientContract;
    }

    if (path.includes(childAllowance)) {
        return onboarding + bankAccount;
    }

    if (path.includes(avsAhv)) {
        return onboarding + childAllowance;
    }

    if (path.includes(additionalInformation)) {
        return onboarding + avsAhv;
    }

    return path;
};
